import { useEffect, useState } from 'react';
import api from '../../adminApi';
import AdminLayout from './AdminLayout';

const fmtDate = (val) => {
    if (!val) return '—';
    try { return new Date(val).toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
    catch { return val; }
};

const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

const AdminContractorListPage = () => {
    const [contractors, setContractors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionLoading, setActionLoading] = useState(null);

    const fetchContractors = () => {
        setLoading(true);
        const params = new URLSearchParams({ search });
        if (statusFilter) params.set('status', statusFilter);
        api.get(`admin/contractors?${params.toString()}`)
            .then(r => setContractors(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchContractors(); }, [search, statusFilter]); // eslint-disable-line

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput);
    };

    const handleApprove = async (contractor) => {
        if (!window.confirm(`${contractor.company_name} を承認しますか？`)) return;
        setActionLoading(contractor.id);
        try {
            await api.post(`admin/contractors/${contractor.id}/approve`);
            fetchContractors();
        } catch { alert('承認に失敗しました'); }
        finally { setActionLoading(null); }
    };

    const handleReject = async (contractor) => {
        if (!window.confirm(`${contractor.company_name} の申請を却下しますか？\nこの操作は取り消せません。`)) return;
        setActionLoading(contractor.id);
        try {
            await api.post(`admin/contractors/${contractor.id}/reject`);
            fetchContractors();
        } catch { alert('却下に失敗しました'); }
        finally { setActionLoading(null); }
    };

    const pendingCount = contractors.filter(c => c.status === 'pending').length;

    return (
        <AdminLayout>
            <div style={{ padding: '32px' }}>

                {/* ヘッダー */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
                    <div>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>業者管理</h2>
                        <p style={{ color: '#6b7280', margin: '4px 0 0', fontSize: '0.875rem' }}>登録業者の管理と審査</p>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <span style={{ padding: '4px 12px', background: '#3b82f6', color: '#fff', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600 }}>
                            登録業者: {contractors.length}
                        </span>
                        {pendingCount > 0 && (
                            <span style={{ padding: '4px 12px', background: '#f59e0b', color: '#fff', borderRadius: 12, fontSize: '0.82rem', fontWeight: 600 }}>
                                審査中: {pendingCount}
                            </span>
                        )}
                    </div>
                </div>

                {/* 検索・フィルタ */}
                <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                    <form onSubmit={handleSearch}>
                        <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>
                                <SearchIcon />
                            </span>
                            <input
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                                placeholder="業者名、メール、業者IDで検索"
                                style={{
                                    width: 320, paddingLeft: 40, paddingRight: 16, paddingTop: 9, paddingBottom: 9,
                                    border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem',
                                    outline: 'none', boxSizing: 'border-box',
                                }}
                            />
                        </div>
                    </form>
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        style={{ padding: '9px 12px', border: '1px solid #e0e4ea', borderRadius: 4, fontSize: '0.875rem', outline: 'none', cursor: 'pointer' }}
                    >
                        <option value="">すべて</option>
                        <option value="pending">審査中のみ</option>
                        <option value="active">承認済みのみ</option>
                        <option value="rejected">却下済みのみ</option>
                    </select>
                </div>

                {/* テーブル */}
                <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e0e4ea' }}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                            <div style={{ width: 32, height: 32, border: '3px solid #e0e4ea', borderTopColor: '#005c53', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                                <thead>
                                    <tr style={{ background: '#f5f6f8', borderBottom: '1px solid #e0e4ea' }}>
                                        {['業者ID', '会社名', 'メールアドレス', '電話番号', '案件数', 'ステータス', '登録日', '操作'].map(h => (
                                            <th key={h} style={{ padding: '10px 20px', textAlign: 'left', color: '#6b7280', fontWeight: 500, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {contractors.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                                                該当する業者がいません
                                            </td>
                                        </tr>
                                    ) : contractors.map(c => (
                                        <tr key={c.id} style={{ borderBottom: '1px solid #e0e4ea', background: c.status === 'pending' ? '#fffbeb' : c.status === 'rejected' ? '#fef2f2' : '' }}
                                            onMouseEnter={e => e.currentTarget.style.background = c.status === 'pending' ? '#fef3c7' : c.status === 'rejected' ? '#fee2e2' : '#f5f6f8'}
                                            onMouseLeave={e => e.currentTarget.style.background = c.status === 'pending' ? '#fffbeb' : c.status === 'rejected' ? '#fef2f2' : ''}>
                                            <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 500, whiteSpace: 'nowrap' }}>#{c.id}</td>
                                            <td style={{ padding: '14px 20px', color: '#1a1a2e', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {c.company_name}
                                                <div style={{ marginTop: 3, fontSize: '0.72rem', fontWeight: 400, color: '#9ca3af' }}>
                                                    許可番号: {c.construction_license_number || '未登録'} ／ インボイス: {c.invoice_registration_number || '未登録'}
                                                </div>
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{c.email}</td>
                                            <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{c.phone || '—'}</td>
                                            <td style={{ padding: '14px 20px', color: '#1a1a2e', whiteSpace: 'nowrap' }}>{c.projects_count}件</td>
                                            <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                {c.status === 'pending' ? (
                                                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: '#ffedd5', color: '#c2410c' }}>審査中</span>
                                                ) : c.status === 'rejected' ? (
                                                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: '#fee2e2', color: '#991b1b' }}>却下済み</span>
                                                ) : (
                                                    <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 500, background: '#dcfce7', color: '#15803d' }}>承認済み</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 20px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtDate(c.created_at)}</td>
                                            <td style={{ padding: '14px 20px', whiteSpace: 'nowrap' }}>
                                                {c.status === 'pending' ? (
                                                    <div style={{ display: 'flex', gap: 8 }}>
                                                        <button
                                                            disabled={actionLoading === c.id}
                                                            onClick={() => handleApprove(c)}
                                                            style={{ padding: '5px 14px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: actionLoading === c.id ? 0.6 : 1 }}
                                                        >
                                                            承認
                                                        </button>
                                                        <button
                                                            disabled={actionLoading === c.id}
                                                            onClick={() => handleReject(c)}
                                                            style={{ padding: '5px 14px', background: 'none', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 4, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', opacity: actionLoading === c.id ? 0.6 : 1 }}
                                                        >
                                                            却下
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </AdminLayout>
    );
};

export default AdminContractorListPage;
